import { Stack, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MapEditorObservationTypeTreeFragment } from "./fragments.generated";

interface MapEditorObservationTypeTreeProps {
    onChange: (values: string[]) => void;
    observationTypes?: MapEditorObservationTypeTreeFragment[];
    value: string[];
}

export function MapEditorObservationTypeTree({ onChange, observationTypes, value }: MapEditorObservationTypeTreeProps) {
    const { t } = useTranslation("mapEditor");

    const elements = useMemo(() => mapToTreeNodes(observationTypes ?? []), [observationTypes]);

    const selectedItems = useMemo(() => {
        const researchTypeIdByObservationTypeId = new Map(
            (observationTypes ?? []).flatMap(researchType =>
                researchType.observationTypes.map(observationType => [observationType.value, researchType.value] as const),
            ),
        );

        return value.flatMap(observationTypeId => {
            const researchTypeId = researchTypeIdByObservationTypeId.get(observationTypeId);
            return researchTypeId !== undefined ? [`${researchTypeId}::${observationTypeId}`] : [];
        });
    }, [value, observationTypes]);

    const handleSelectedItemsChange = useCallback(
        (_: unknown, selection: string[]) => {
            const observationTypeIds = selection
                .map(element => element.split("::")[1])
                .filter(observationTypeId => observationTypeId !== undefined);

            onChange(observationTypeIds);
        },
        [onChange],
    );

    return (
        <Stack direction="column" rowGap={1}>
            <Typography sx={{ paddingInline: 3 / 2 }} variant="overline">
                {t("observationTypeTree.label")}
            </Typography>

            <RichTreeView
                checkboxSelection
                multiSelect
                items={elements}
                onSelectedItemsChange={handleSelectedItemsChange}
                selectedItems={selectedItems}
                selectionPropagation={{
                    descendants: true,
                    parents: true,
                }}
            />
        </Stack>
    );
}

function mapToTreeNodes(researchTypes: MapEditorObservationTypeTreeFragment[]) {
    return researchTypes.map(researchType => ({
        id: researchType.value,
        label: researchType.label,
        children: researchType.observationTypes.map(observationType => ({
            id: `${researchType.value}::${observationType.value}`,
            label: observationType.label,
        })),
    }));
}
